<template>
  <div class="operation-logs">
    <h2>操作日志管理</h2>
    
    <!-- 统计卡片 -->
    <div class="stats-cards">
      <div class="stat-card">
        <div class="stat-value">{{ stats.todayCount }}</div>
        <div class="stat-label">今日操作</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ stats.weekCount }}</div>
        <div class="stat-label">本周操作</div>
      </div>
      <div class="stat-card" v-for="type in stats.typeStats" :key="type.operation_type">
        <div class="stat-value">{{ type.count }}</div>
        <div class="stat-label">{{ type.operation_type }}</div>
      </div>
    </div>
    
    <!-- 筛选条件 -->
    <div class="filter-bar">
      <div class="filter-row">
        <select v-model="filters.operationType">
          <option value="">全部操作类型</option>
          <option value="INSERT">新增</option>
          <option value="UPDATE">更新</option>
          <option value="DELETE">删除</option>
        </select>
        
        <select v-model="filters.tableName">
          <option value="">全部表名</option>
          <option value="dishes">菜品表</option>
          <option value="dish_categories">分类表</option>
          <option value="users">用户表</option>
        </select>
        
        <input type="text" v-model="filters.userId" placeholder="用户ID" />
        
        <input type="date" v-model="filters.startTime" placeholder="开始时间" />
        <span>至</span>
        <input type="date" v-model="filters.endTime" placeholder="结束时间" />
        
        <button @click="searchLogs" class="btn-search">搜索</button>
        <button @click="resetFilters" class="btn-reset">重置</button>
        <button @click="showClearConfirm" class="btn-clear">清空日志</button>
      </div>
    </div>
    
    <!-- 日志表格 -->
    <div class="table-wrapper">
      <table class="log-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>用户ID</th>
            <th>操作类型</th>
            <th>表名</th>
            <th>记录ID</th>
            <th>旧数据</th>
            <th>新数据</th>
            <th>操作时间</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="log in logList" :key="log.id">
            <td>{{ log.id }}</td>
            <td>{{ log.user_id }}</td>
            <td>
              <span :class="getOpTypeClass(log.operation_type)">
                {{ getOpTypeText(log.operation_type) }}
              </span>
            </td>
            <td>{{ log.table_name }}</td>
            <td>{{ log.record_id || '-' }}</td>
            <td class="data-cell">
              <div v-if="log.old_data" class="data-preview" @click="showDetail(log.old_data, '旧数据')">
                查看详情
              </div>
              <span v-else>-</span>
            </td>
            <td class="data-cell">
              <div v-if="log.new_data" class="data-preview" @click="showDetail(log.new_data, '新数据')">
                查看详情
              </div>
              <span v-else>-</span>
            </td>
            <td>{{ formatTime(log.created_at) }}</td>
          </tr>
          <tr v-if="logList.length === 0 && !loading">
            <td colspan="8" class="empty-data">暂无数据</td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <!-- 分页 -->
    <div class="pagination" v-if="total > 0">
      <button @click="changePage(1)" :disabled="page === 1">首页</button>
      <button @click="changePage(page - 1)" :disabled="page === 1">上一页</button>
      <span>第 {{ page }} / {{ totalPages }} 页</span>
      <button @click="changePage(page + 1)" :disabled="page === totalPages">下一页</button>
      <button @click="changePage(totalPages)" :disabled="page === totalPages">末页</button>
    </div>
    
    <!-- 数据详情弹窗 -->
    <div v-if="showDetailModal" @click.self="closeDetailModal" class="modal-overlay">
      <div class="modal detail-modal">
        <h3>{{ detailTitle }}</h3>
        <pre class="json-viewer">{{ formatJSON(detailData) }}</pre>
        <button @click="closeDetailModal" class="close-detail-btn">关闭</button>
      </div>
    </div>
    
    <!-- 清空确认弹窗 -->
    <div v-if="showClearModal" @click.self="closeClearModal" class="modal-overlay">
      <div class="modal clear-modal">
        <h3 style="color: #f56c6c;">⚠️ 清空操作日志</h3>
        <p>此操作将永久删除所有操作日志，不可恢复！</p>
        <p>请输入 <strong>"确认清空"</strong> 以继续：</p>
        <input type="text" v-model="clearConfirmText" placeholder="输入 确认清空" />
        <div class="modal-buttons">
          <button @click="closeClearModal">取消</button>
          <button @click="confirmClear" :disabled="clearConfirmText !== '确认清空'" class="btn-confirm">
            确认清空
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getOperationLogs, getOperationLogsStats, clearOperationLogs } from "@/api/houtai/index";

export default {
  name: "OperationLogs",
  data() {
    return {
      logList: [],
      page: 1,
      pageSize: 20,
      total: 0,
      loading: false,
      filters: {
        operationType: '',
        tableName: '',
        userId: '',
        startTime: '',
        endTime: ''
      },
      stats: {
        typeStats: [],
        tableStats: [],
        todayCount: 0,
        weekCount: 0
      },
      showDetailModal: false,
      detailTitle: '',
      detailData: null,
      showClearModal: false,
      clearConfirmText: ''
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize);
    }
  },
  mounted() {
    this.fetchLogs();
    this.fetchStats();
  },
  methods: {
    formatTime(time) {
      if (!time) return '-';
      return new Date(time).toLocaleString('zh-CN');
    },
    
    formatJSON(data) {
      try {
        const obj = typeof data === 'string' ? JSON.parse(data) : data;
        return JSON.stringify(obj, null, 2);
      } catch (e) {
        return data;
      }
    },
    
    getOpTypeClass(type) {
      const classes = {
        'INSERT': 'op-insert',
        'UPDATE': 'op-update',
        'DELETE': 'op-delete'
      };
      return classes[type] || '';
    },
    
    getOpTypeText(type) {
      const texts = {
        'INSERT': '新增',
        'UPDATE': '更新',
        'DELETE': '删除'
      };
      return texts[type] || type;
    },
    
    async fetchLogs() {
      this.loading = true;
      try {
        const params = {
          page: this.page,
          pageSize: this.pageSize,
          ...this.filters
        };
        const res = await getOperationLogs(params);
        if (res.status === 200) {
          this.logList = res.data;
          this.total = res.total;
        }
      } catch (error) {
        console.error('获取日志失败:', error);
        this.$message.error('获取日志失败');
      } finally {
        this.loading = false;
      }
    },
    
    async fetchStats() {
      try {
        const res = await getOperationLogsStats();

        if (res.status === 200) {
        console.log('统计数据:', res);

          this.stats = res.data;
        }
      } catch (error) {
        console.error('获取统计失败:', error);
      }
    },
    
    searchLogs() {
      this.page = 1;
      this.fetchLogs();
    },
    
    resetFilters() {
      this.filters = {
        operationType: '',
        tableName: '',
        userId: '',
        startTime: '',
        endTime: ''
      };
      this.page = 1;
      this.fetchLogs();
    },
    
    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.page = page;
      this.fetchLogs();
    },
    
    showDetail(data, title) {
      this.detailTitle = title;
      this.detailData = data;
      this.showDetailModal = true;
    },
    
    closeDetailModal() {
      this.showDetailModal = false;
      this.detailData = null;
    },
    
    showClearConfirm() {
      this.clearConfirmText = '';
      this.showClearModal = true;
    },
    
    closeClearModal() {
      this.showClearModal = false;
      this.clearConfirmText = '';
    },
    
    async confirmClear() {
      if (this.clearConfirmText !== '确认清空') return;
      
      try {
        const res = await clearOperationLogs({ confirm: '确认清空' });
        if (res.data.code === 0) {
          this.$message.success('操作日志已清空');
          this.closeClearModal();
          this.fetchLogs();
          this.fetchStats();
        } else {
          this.$message.error(res.data.msg);
        }
      } catch (error) {
        console.error('清空失败:', error);
        this.$message.error('清空失败');
      }
    },
    
    
  }
};
</script>

<style scoped>
.operation-logs {
  padding: 20px;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
  margin-top: 23px;
}

.stat-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 15px;
  border-radius: 10px;
  text-align: center;
}

.stat-value {
  font-size: 28px;
  font-weight: bold;
}

.stat-label {
  font-size: 14px;
  margin-top: 5px;
  opacity: 0.9;
}

.filter-bar {
  background: #f5f7fa;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.filter-row select,
.filter-row input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.table-wrapper {
  overflow-x: auto;
}

.log-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.log-table th,
.log-table td {
  padding: 12px;
  text-align: center;
  border-bottom: 1px solid #f0f0f0;
}

.log-table th {
  background: #f5f7fa;
  font-weight: 600;
}

.data-cell {
  max-width: 150px;
}

.data-preview {
  color: #409eff;
  cursor: pointer;
  text-decoration: underline;
}

.data-preview:hover {
  color: #66b1ff;
}

.op-insert {
  color: #67c23a;
  font-weight: bold;
}

.op-update {
  color: #e6a23c;
  font-weight: bold;
}

.op-delete {
  color: #f56c6c;
  font-weight: bold;
}

.empty-data {
  text-align: center;
  padding: 40px;
  color: #999;
}

.pagination {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
  flex-wrap: wrap;
}

.pagination button {
  padding: 6px 12px;
  border: 1px solid #ddd;
  cursor: pointer;
  border-radius: 4px;
}

.pagination button:hover:not(:disabled) {
  background: #409eff;
  color: white;
}

.pagination button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 8px;
  padding: 20px;
  max-width: 90%;
  margin: 20px auto;
  max-height: 80vh;
  overflow-y: auto;
  display: flex;
    flex-direction: column;
  
}

.detail-modal {
  width: 600px;
}

.clear-modal {
  width: 400px;
  text-align: center;
}

.json-viewer {
  background: #f5f7fa;
  padding: 15px;
  border-radius: 4px;
  font-size: 12px;
  overflow-x: auto;
  max-height: 400px;
  width: 90%;
  margin: 0 auto;
}

.modal-buttons {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 20px;
}

.modal-buttons button {
  padding: 8px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.btn-confirm {
  background: #f56c6c;
  color: white;
}

.btn-confirm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-search, .btn-reset, .btn-export, .btn-clear {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: white;
}

.btn-search {
  background: #409eff;
}

.btn-reset {
  background: #909399;
}

.btn-export {
  background: #67c23a;
}

.btn-clear {
  background: #f56c6c;
}

.close-detail-btn {
  margin-top: 15px;
  padding: 8px 20px;
  background: #409eff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

@media (max-width: 768px) {
  .stats-cards {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .filter-row {
    flex-direction: column;
    align-items: stretch;
  }
  
  .detail-modal {
    width: 95%;
  }
}
</style>